"use client";

import { useState, useEffect, useCallback, useMemo } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  CreditCard,
  FileText,
  Smartphone,
  Coins,
  ChevronDown,
  ArrowLeft,
} from "lucide-react";
import { toast } from "sonner";
import { useCompanyEdit } from "@/hooks/use-company-edit";
import {
  STATUS_IDS,
  getStatusColorClasses,
  getStatusColorClassesByName,
} from "@/lib/constants/statuses";
import { CUSTOMER_TYPES, CUSTOMER_TYPE_IDS } from "@/lib/constants/customer-types";
import { BOOKING_TYPE_IDS } from "@/lib/constants/booking-types";

// Import all components
import { BookingInfoCard } from "@/components/booking/details/BookingInfoCard";
import { ClientInfoCard } from "@/components/booking/details/ClientInfoCard";
import { CompanyInfoCard } from "@/components/booking/details/CompanyInfoCard";
import { AddOnsCard } from "@/components/booking/details/AddOnsCard";
import { ShopItemsCard } from "@/components/booking/details/ShopItemsCard";
import { ShowsCard } from "@/components/booking/details/ShowsCard";
import { PriceSummaryCard } from "@/components/booking/details/PriceSummaryCard";
import { PaymentInfoCard } from "@/components/booking/details/PaymentInfoCard";
import { ExternalNotesCard } from "@/components/booking/details/ExternalNotesCard";
import { NotesTab } from "@/components/booking/details/NotesTab";
import { ActivityTab } from "@/components/booking/details/ActivityTab";
import {
  EditBookingStatusModal,
  EditPaymentStatusModal,
  EditPaymentMethodModal,
  CancelBookingModal,
  ArchiveBookingModal,
  SendEmailModal,
  EditHostModal,
  EditClientModal,
  EditCompanyModal,
  EditLanguageModal,
} from "@/components/booking/details/modals";
import { EditDateTimeModal } from "@/components/booking/details/EditDateTimeModal";

interface BookingClientProps {
  venueId: string;
  bookingId: string;
  fromVenueId?: string;
  venueDefaultLanguage?: string;
}

// Optimized data fetcher with caching
const fetcher = async (url: string) => {
  // Add timeout to prevent hanging requests
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout

  try {
    const response = await fetch(url, {
      signal: controller.signal,
      headers: {
        "Content-Type": "application/json",
      },
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      // Try to parse as JSON, but handle HTML error pages
      const contentType = response.headers.get("content-type");
      if (contentType && contentType.includes("application/json")) {
        const error = await response.json();
        throw new Error(
          error.error || `Failed to fetch: ${response.statusText}`,
        );
      } else {
        // If not JSON, it's likely an HTML error page
        throw new Error(
          `Failed to fetch: ${response.status} ${response.statusText}`,
        );
      }
    }

    const data = await response.json();
    return data;
  } catch (error) {
    clearTimeout(timeoutId);
    if ((error as Error).name === "AbortError") {
      throw new Error("Request timed out - please try again");
    }
    throw error;
  }
};

// Simple in-memory cache
const cache = new Map();
const cacheTimeout = 1 * 1000; // 1 second

const cachedFetch = async (url: string, forceRefresh = false) => {
  const cacheKey = url;

  // If force refresh, clear the cache for this key first
  if (forceRefresh) {
    cache.delete(cacheKey);
  }

  const cached = cache.get(cacheKey);

  if (!forceRefresh && cached && Date.now() - cached.timestamp < cacheTimeout) {
    return cached.data;
  }

  const data = await fetcher(url);
  cache.set(cacheKey, { data, timestamp: Date.now() });
  return data;
};

export default function BookingClient({
  venueId,
  bookingId,
  fromVenueId,
  venueDefaultLanguage = "en",
}: BookingClientProps) {
  const router = useRouter();

  // Core booking data
  const [booking, setBooking] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Related data
  const [bookingStatuses, setBookingStatuses] = useState<any[]>([]);
  const [paymentStatuses, setPaymentStatuses] = useState<any[]>([]);
  const [activities, setActivities] = useState<any[]>([]);
  const [activitiesLoading, setActivitiesLoading] = useState(false);
  const [notes, setNotes] = useState<any[]>([]);
  const [availableHosts, setAvailableHosts] = useState<any[]>([]);
  const [hasVenueAddOns, setHasVenueAddOns] = useState<boolean | null>(null);
  const [hasVenueShopItems, setHasVenueShopItems] = useState<boolean | null>(
    null,
  );
  const [invoiceData, setInvoiceData] = useState<any>(null);
  const [invoiceLoading, setInvoiceLoading] = useState<boolean>(true);

  // UI state
  const [activeRightTab, setActiveRightTab] = useState("notes");
  const [isEditBookingStatusModalOpen, setIsEditBookingStatusModalOpen] =
    useState(false);
  const [isEditPaymentStatusModalOpen, setIsEditPaymentStatusModalOpen] =
    useState(false);
  const [isEditPaymentMethodModalOpen, setIsEditPaymentMethodModalOpen] =
    useState(false);
  const [isEditHostModalOpen, setIsEditHostModalOpen] = useState(false);
  const [isEditClientModalOpen, setIsEditClientModalOpen] = useState(false);
  const [isEditDateTimeModalOpen, setIsEditDateTimeModalOpen] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [archiveReason, setArchiveReason] = useState("");
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");

  // Form state
  const [selectedBookingStatus, setSelectedBookingStatus] = useState("");
  const [selectedPaymentStatus, setSelectedPaymentStatus] = useState("");
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState("");
  const [selectedHost, setSelectedHost] = useState("");
  const [statusNote, setStatusNote] = useState("");
  const [newNote, setNewNote] = useState("");
  const [addingNote, setAddingNote] = useState(false);
  const [currentUserInfoId, setCurrentUserInfoId] = useState<number | null>(null);
  const [currentUserRoleId, setCurrentUserRoleId] = useState<number | null>(null);
  const [updatingPaymentMethod, setUpdatingPaymentMethod] = useState(false);
  const [isEditLanguageModalOpen, setIsEditLanguageModalOpen] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState("");
  const [updatingLanguage, setUpdatingLanguage] = useState(false);
  const [venueLanguages, setVenueLanguages] = useState<string[]>([]);
  const [roadShowVenueDetails, setRoadShowVenueDetails] = useState<any>(null);

  // Client form data
  const [clientFormData, setClientFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    participants: undefined as number | undefined,
  });

  // Permission state
  const [canEdit, setCanEdit] = useState(true);
  const [canViewPaymentDetails, setCanViewPaymentDetails] = useState(true);

  // Fetch booking data
  const fetchBookingData = useCallback(
    async (forceRefresh = false) => {
      try {
        setLoading(true);
        setError(null);
        const response = await cachedFetch(
          `/api/staff/venues/${venueId}/bookings/${bookingId}`,
          forceRefresh,
        );

        if (response.success) {
          setBooking(response.data);

          // Use metadata to control UI permissions
          const metadata = response.data._metadata;
          if (metadata) {
            setCanEdit(metadata.canEdit || false);
            setCanViewPaymentDetails(metadata.canViewPaymentDetails || false);
          }

          // Set client details
          const customer = response.data.customer;
          if (customer) {
            setClientFormData({
              fullName: customer.name || "",
              email: customer.email || "",
              phone: customer.phone || "",
              participants: response.data.booking?.participant_count || undefined,
            });
          }
        } else {
          // Check if booking belongs to a different venue
          if (response.wrongVenue && response.actualVenueId) {
            // Show error message and redirect to venue dashboard
            const currentPath = window.location.pathname;
            const pathParts = currentPath.split("/");
            const portal = pathParts[1]; // booking, superadmin, etc.

            // Instead of redirecting to the booking in the correct venue,
            // redirect to the current venue's dashboard since the booking doesn't belong here
            const dashboardUrl = `/${portal}/venue/${venueId}/dashboard`;

            setError(`This booking belongs to a different venue. Redirecting to dashboard...`);

            // Small delay to show the error message before redirecting
            setTimeout(() => {
              router.push(dashboardUrl);
            }, 1500);
            return;
          }
          setError(response.error || "Failed to load booking");
        }
      } catch (err) {
        setError((err as Error).message || "Failed to load booking details");
      } finally {
        setLoading(false);
      }
    },
    [bookingId, venueId],
  );

  // Fetch activities
  const fetchActivities = useCallback(async () => {
    if (activeRightTab !== "activities") return;

    try {
      setActivitiesLoading(true);
      const response = await fetcher(
        `/api/staff/venues/${venueId}/bookings/${bookingId}/activities`,
      );
      if (response.success) {
        const activitiesData = response.data || [];

        // Add booking creation activity from booking data if we have it
        if (booking && booking.created_at) {
          let creatorName = "System";

          // If we have created_by (users_infos id), fetch the user name
          if (booking.created_by) {
            try {
              // Fetch the creator's name from users_infos
              const creatorResponse = await fetch(`/api/users/${booking.created_by}/info`);
              if (creatorResponse.ok) {
                const creatorData = await creatorResponse.json();
                if (creatorData.success && creatorData.data?.name) {
                  creatorName = creatorData.data.name;
                }
              }
            } catch (error) {
              console.error("Failed to fetch creator info:", error);
              // If fetch fails, check if it was created online
              if (!booking.created_by && booking.creator) {
                creatorName = "Guest (Online)";
              }
            }
          } else if (booking.creator && !booking.created_by) {
            // Has creator UUID but no created_by - created online
            creatorName = "Guest (Online)";
          }

          const creationActivity = {
            id: `creation-${booking.id}`,
            activity_type: "booking_created",
            activity_detail: {
              type: "booking_created",
              changed_by: creatorName,
              booking_id: booking.id,
            },
            created_at: booking.created_at,
            created_by_name: creatorName,
            details: {
              type: "booking_created",
            },
          };

          // Add creation activity at the end (oldest activity)
          const allActivities = [...activitiesData, creationActivity];

          // Sort by created_at descending (newest first)
          allActivities.sort(
            (a, b) =>
              new Date(b.created_at).getTime() -
              new Date(a.created_at).getTime(),
          );

          setActivities(allActivities);
        } else {
          setActivities(activitiesData);
        }
      }
    } catch (err) {
    } finally {
      setActivitiesLoading(false);
    }
  }, [bookingId, activeRightTab, venueId, booking]);

  // Editing the customer's company profile (shared with the bar staff page).
  const companyEdit = useCompanyEdit({
    booking,
    venueId,
    bookingId,
    // Super admin and booking staff; read-only users get canEdit = false.
    canEditPartner: canEdit,
    onSaved: async () => {
      // Refresh booking details to show updated company information
      await fetchBookingData(true);
      // Refresh activities if that tab is active
      if (activeRightTab === "activities") {
        fetchActivities();
      }
    },
  });

  // Load static data
  const loadStaticData = useCallback(async () => {
    try {
      const [bookingStatusesRes, paymentStatusesRes, usersRes, venueDetailsRes] =
        await Promise.all([
          cachedFetch("/api/statuses?type=booking"),
          cachedFetch("/api/statuses?type=payment"),
          cachedFetch(`/api/staff/venues/${venueId}/users`),
          cachedFetch(`/api/venues/${venueId}/details`),
        ]);

      // Filter and deduplicate booking statuses
      const allBookingStatuses = bookingStatusesRes.data?.all || [];

      // Valid booking status IDs according to the status constants
      const validBookingStatusIds = [
        STATUS_IDS.BOOKING_PRELIMINARY,
        STATUS_IDS.BOOKING_CONFIRMED,
        STATUS_IDS.BOOKING_CANCELLED,
        STATUS_IDS.BOOKING_COMPLETED,
        STATUS_IDS.BOOKING_NO_SHOW,
        STATUS_IDS.BOOKING_ARCHIVED,
        STATUS_IDS.BOOKING_DRAFT,
      ];

      // Filter to only include valid booking statuses and remove duplicates
      const uniqueBookingStatuses = allBookingStatuses
        .filter((status: any) => validBookingStatusIds.includes(status.id))
        .filter(
          (status: any, index: number, self: any[]) =>
            index === self.findIndex((s: any) => s.id === status.id),
        );

      setBookingStatuses(uniqueBookingStatuses);
      setPaymentStatuses(paymentStatusesRes.data?.all || []);

      // Get all users from the customers endpoint
      // The customers API returns { success: boolean, data: [...] }
      const allUsers = usersRes.success ? usersRes.data || [] : [];

      // The API already returns only hosts, no need to filter
      // Hosts have role_id === 7
      const hosts = usersRes.success && usersRes.data ? usersRes.data : [];

      setAvailableHosts(hosts);

      // Set venue languages
      if (venueDetailsRes.success && venueDetailsRes.data?.languages) {
        setVenueLanguages(venueDetailsRes.data.languages);
      }
    } catch (err) {}
  }, [venueId]);

  // Initial load
  useEffect(() => {
    fetchBookingData();
    loadStaticData();
  }, [fetchBookingData, loadStaticData]);

  // Re-fetch booking data when venueId changes
  // This ensures that when a venue is switched, the booking is re-validated
  useEffect(() => {
    if (venueId && bookingId) {
      fetchBookingData();
    }
  }, [venueId, bookingId, fetchBookingData]);

  // Fetch notes
  const fetchNotes = useCallback(async () => {
    if (activeRightTab !== "notes") return;

    try {
      const response = await fetcher(
        `/api/staff/venues/${venueId}/bookings/${bookingId}/notes`,
      );
      if (response.success) {
        setNotes(response.data || []);
        if (typeof response.currentUserInfoId === "number") {
          setCurrentUserInfoId(response.currentUserInfoId);
        }
        if (typeof response.currentUserRoleId === "number") {
          setCurrentUserRoleId(response.currentUserRoleId);
        }
      }
    } catch (err) {
      console.error("Error fetching notes:", err);
    }
  }, [bookingId, activeRightTab, venueId]);

  // Load activities or notes when tab changes
  useEffect(() => {
    if (activeRightTab === "activities") {
      fetchActivities();
    } else if (activeRightTab === "notes") {
      fetchNotes();
    }
  }, [activeRightTab, fetchActivities, fetchNotes]);

  // Check if venue has add-ons
  const checkVenueAddOns = useCallback(async () => {
    try {
      const response = await fetch(`/api/venues/${venueId}/add-ons`);
      if (response.ok) {
        const data = await response.json();
        setHasVenueAddOns(Array.isArray(data) && data.length > 0);
      } else if (response.status === 401 || response.status === 403) {
        // User doesn't have permission to check add-ons, assume they exist to show the section
        setHasVenueAddOns(true);
      } else {
        setHasVenueAddOns(false);
      }
    } catch (error) {
      // On error, assume add-ons exist to show the section (better UX)
      setHasVenueAddOns(true);
    }
  }, [venueId]);

  // Check if venue has shop items
  const checkVenueShopItems = useCallback(async () => {
    try {
      const response = await fetch(`/api/venues/${venueId}/shop-items`);
      if (response.ok) {
        const data = await response.json();
        setHasVenueShopItems(Array.isArray(data) && data.length > 0);
      } else if (response.status === 401 || response.status === 403) {
        // User doesn't have permission to check shop items, assume they exist to show the section
        setHasVenueShopItems(true);
      } else {
        setHasVenueShopItems(false);
      }
    } catch (error) {
      // On error, assume shop items exist to show the section (better UX)
      setHasVenueShopItems(true);
    }
  }, [venueId]);

  // Check if invoice exists for this booking
  const checkInvoiceExists = useCallback(
    async (syncFortnoxStatus = false) => {
      try {
        setInvoiceLoading(true);
        // Add sync=true parameter to fetch live status from Fortnox and update payment status
        const url = syncFortnoxStatus
          ? `/api/bookings/${bookingId}/invoice?sync=true`
          : `/api/bookings/${bookingId}/invoice`;

        const response = await fetch(url);
        if (response.ok) {
          const data = await response.json();

          // If invoice doesn't exist, set to null
          if (!data.invoice) {
            setInvoiceData(null);
          } else {
            // Invoice exists, merge with Fortnox data
            setInvoiceData({
              ...data.invoice,
              fortnoxStatus: data.fortnoxStatus,
              fortnoxError: data.fortnoxError,
            });
          }

          // If we synced status and payment was updated, refresh the booking data
          if (syncFortnoxStatus && data.fortnoxStatus) {
            fetchBookingData(true);
          }
        } else {
          setInvoiceData(null);
        }
      } catch (error) {
        console.error("Error checking invoice:", error);
        setInvoiceData(null);
      } finally {
        setInvoiceLoading(false);
      }
    },
    [bookingId, fetchBookingData],
  );

  // Check venue add-ons, shop items, and invoice on mount
  useEffect(() => {
    checkVenueAddOns();
    checkVenueShopItems();
    checkInvoiceExists();
  }, [checkVenueAddOns, checkVenueShopItems, checkInvoiceExists]);

  // State for actual venue currency (for road shows)
  const [actualVenueCurrency, setActualVenueCurrency] = useState<string | null>(null);

  // Fetch actual venue details for road shows (currency, cost center, accounting credentials)
  useEffect(() => {
    const fetchRoadShowVenueDetails = async () => {
      // Check if this is a road show
      const hostBooking = booking?.studio_show_host_bookings?.[0];
      const isRoadShow = hostBooking?.booking_type_id === BOOKING_TYPE_IDS.ON_ROAD;
      const studio = hostBooking?.studios;

      // Determine which venue ID to use
      let venueIdToFetch = null;
      if (isRoadShow && booking?.venue_id) {
        // For road shows, use booking.venue_id directly
        venueIdToFetch = booking.venue_id;
      } else if (!isRoadShow && studio?.venue_id) {
        // For corporate bookings, use studio's venue_id
        venueIdToFetch = studio.venue_id;
      }

      // Fetch venue details for both road shows and corporate bookings
      if (venueIdToFetch) {
        try {
          const response = await fetch(`/api/venues/${venueIdToFetch}/details`);
          if (response.ok) {
            const result = await response.json();

            // The API returns {success: true, data: {venue details}}
            const venueData = result.data || result;

            if (venueData.currency) {
              setActualVenueCurrency(venueData.currency);
            } else if (venueData.venue?.currency) {
              setActualVenueCurrency(venueData.venue.currency);
            }

            // Store venue name, cost center, and accounting credentials for both road shows and corporate bookings
            const venueName = venueData.name || venueData.venue?.name || null;
            const costCenter = venueData.cost_center || venueData.venue?.cost_center || null;
            const accountingCredentials = venueData.accounting_credentials || venueData.venue?.accounting_credentials || null;
            const accountings = venueData.accountings || venueData.venue?.accountings || null;

            setRoadShowVenueDetails({
              name: venueName,
              costCenter: costCenter,
              accounting_credentials: accountingCredentials,
              accountings,
            });
          }
        } catch (error) {
          console.error('Failed to fetch venue details:', error);
        }
      }
    };

    if (booking) {
      fetchRoadShowVenueDetails();
      fetchRoadShowVenueDetails();
    }
  }, [booking]);

  // Derive booking details
  const bookingDetails = useMemo(() => {
    if (!booking) return null;

    const hostBooking = booking.studio_show_host_bookings?.[0];
    const timeSlotPricing = hostBooking?.show_time_slot_pricing;
    const studio = hostBooking?.studios;
    const show = timeSlotPricing?.shows;
    const venue = studio?.venues;
    const pricePoint = timeSlotPricing?.show_price_points;

    // Helper function to convert 24-hour time to 12-hour format
    const convertTo12Hour = (time24: string): string => {
      const [hours, minutes] = time24.split(':').map(Number);
      const period = hours >= 12 ? 'PM' : 'AM';
      const hours12 = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
      return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
    };

    // Parse date and time from the session timeslot
    let showDate = null;
    let showTime = null;
    let showTime12Hour = null;
    if (hostBooking?.show_session_timeslot) {
      const [datePart, timePart] = hostBooking.show_session_timeslot.split("T");
      showDate = datePart;
      if (timePart) {
        showTime = timePart.substring(0, 5); // Get HH:MM from HH:MM:SS.sssZ
        showTime12Hour = convertTo12Hour(showTime);
      }
    }

    // For road shows, check multiple sources for time range
    let timeRange = null;
    if (hostBooking?.booking_type_id === BOOKING_TYPE_IDS.ON_ROAD) {
      // First check the details field for properly stored road show times
      let roadShowDetails = null;
      try {
        if (booking.details) {
          roadShowDetails = typeof booking.details === 'string'
            ? JSON.parse(booking.details)
            : booking.details;
        }
      } catch (e) {
        // Ignore parsing errors
      }

      if (roadShowDetails?.roadShowEndTime && roadShowDetails?.roadShowStartTime) {
        // Use properly stored times from details
        const startTime12 = convertTo12Hour(roadShowDetails.roadShowStartTime);
        const endTime12 = convertTo12Hour(roadShowDetails.roadShowEndTime);
        timeRange = `${startTime12} - ${endTime12}`;
      } else if (booking.special_request) {
        // Fallback to parsing from special_request field
        const timeMatch = booking.special_request.match(/Time:\s*(\d{1,2}:\d{2})\s*-\s*(\d{1,2}:\d{2})/);
        if (timeMatch) {
          // Convert both times to 12-hour format
          const startTime12 = convertTo12Hour(timeMatch[1]);
          const endTime12 = convertTo12Hour(timeMatch[2]);
          timeRange = `${startTime12} - ${endTime12}`;
        } else if (showTime) {
          // If no range found, show start time with a default end time
          const defaultEndTime = convertTo12Hour("17:00");
          timeRange = `${showTime12Hour} - ${defaultEndTime}`;
        }
      } else if (showTime) {
        // If no special_request at all, show start time with a default end time
        const defaultEndTime = convertTo12Hour("17:00");
        timeRange = `${showTime12Hour} - ${defaultEndTime}`;
      }
    }

    // Use fetched venue currency if available, otherwise fallback to studio's venue currency
    const isRoadShowBooking = hostBooking?.booking_type_id === BOOKING_TYPE_IDS.ON_ROAD;
    const bookingCurrency = isRoadShowBooking && actualVenueCurrency
      ? actualVenueCurrency
      : (venue?.currency || "SEK");

    // Use venue name from booking data (already includes venue_infos with translations)
    // For road shows, prefer fetched venue details; for corporate, use booking data
    const displayVenueName = isRoadShowBooking && roadShowVenueDetails?.name
      ? roadShowVenueDetails.name
      : (venue?.venue_infos?.[0]?.name || venue?.name || "Unknown venue");

    // Use cost center from fetched details or booking data
    const displayCostCenter = roadShowVenueDetails?.costCenter || venue?.cost_center;

    // Use accounting credentials from fetched venue details or booking data
    const accountingCredentials = roadShowVenueDetails?.accounting_credentials || venue?.accounting_credentials?.additional_config;
    const accountings = roadShowVenueDetails?.accountings || venue?.accountings || null;

    return {
      showName: show?.show_infos?.[0]?.name || "No show assigned",
      studioName: studio?.name || "Unknown studio",
      venueName: displayVenueName,
      costCenter: displayCostCenter,
      timeSlot: timeSlotPricing?.time_slot || "Not specified",
      hostName: booking.host?.name || "No host assigned", // Host is on booking, not hostBooking
      currency: bookingCurrency,
      pricing: booking.pricing || pricePoint?.pricings,
      sessionDate: hostBooking?.show_session_timeslot,
      showDate: showDate,
      showTime: timeRange || showTime || null,
      minChargeParticipants: show?.min_charge_participants || null,
      accounting_credentials: accountingCredentials,
      accountings,
    };
  }, [booking, actualVenueCurrency, roadShowVenueDetails]);

  // Check if this is a road show booking
  const isRoadShow = useMemo(() => {
    const hostBooking = booking?.studio_show_host_bookings?.[0];
    return hostBooking?.booking_type_id === BOOKING_TYPE_IDS.ON_ROAD;
  }, [booking]);

  // Status color helper - uses centralized color system
  const getStatusColor = (statusName: string, statusId?: number) => {
    // If we have a status ID, use it for accurate color mapping
    if (statusId) {
      return getStatusColorClasses(statusId);
    }
    // Fallback to name-based mapping
    return getStatusColorClassesByName(statusName);
  };

  // Payment icon helper
  const getPaymentIcon = (method: string) => {
    switch (method) {
      case "card":
        return <CreditCard className="w-4 h-4" />;
      case "invoice":
        return <FileText className="w-4 h-4" />;
      case "swish":
        return <Smartphone className="w-4 h-4" />;
      case "cash":
        return <Coins className="w-4 h-4" />;
      default:
        return <CreditCard className="w-4 h-4" />;
    }
  };

  // Handle status updates
  const handleBookingStatusUpdate = async () => {
    try {
      const oldStatus = booking.booking_status;
      const newStatus = bookingStatuses.find(
        (s) => s.id.toString() === selectedBookingStatus,
      );

      // Validation: Check if trying to complete a future booking
      if (parseInt(selectedBookingStatus) === STATUS_IDS.BOOKING_COMPLETED) {
        // Get the show start time from the booking
        const showStartTime = booking.studio_show_host_bookings?.[0]?.show_session_timeslot ||
                              booking.show_session_timeslot ||
                              booking.booking_date;

        if (showStartTime) {
          const showStartDate = new Date(showStartTime);
          const now = new Date();

          // Check if the show is in the future
          if (showStartDate > now) {
            const formattedDate = showStartDate.toLocaleDateString('en-US', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            });

            toast.error(
              `Cannot mark as completed: This booking is scheduled for ${formattedDate}, which is in the future. Bookings can only be marked as completed after the show time has passed.`,
              {
                duration: 6000, // Show for 6 seconds
              }
            );
            return; // Stop execution
          }
        }
      }

      const requestBody = {
        status_id: parseInt(selectedBookingStatus),
        old_status_id: oldStatus?.id,
        old_status_name: oldStatus?.name,
        new_status_name: newStatus?.name,
        reason: statusNote || "",
      };

      const response = await fetch(
        `/api/staff/venues/${venueId}/bookings/${bookingId}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(requestBody),
        },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: "Failed to update status" }));
        throw new Error(errorData.error || errorData.details || "Failed to update status");
      }

      // Update local state instead of refetching
      setBooking((prev: any) => ({
        ...prev,
        status_id: parseInt(selectedBookingStatus),
        booking_status: newStatus,
      }));

      toast.success("Booking status updated");

      // Refresh activities if that tab is active
      if (activeRightTab === "activities") {
        fetchActivities();
      }

      setIsEditBookingStatusModalOpen(false);
      setStatusNote("");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to update booking status");
    }
  };

  const handlePaymentStatusUpdate = async () => {
    try {
      const oldStatus = booking.payment_status;
      const newStatus = paymentStatuses.find(
        (s) => s.id.toString() === selectedPaymentStatus,
      );

      const response = await fetch(
        `/api/bookings/${bookingId}/payment-status`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            payment_status_id: parseInt(selectedPaymentStatus),
            old_payment_status_id: oldStatus?.id || null,
            old_payment_status_name: oldStatus?.name || "Pending",
            new_payment_status_name: newStatus?.name || "Paid",
            reason: statusNote || "",
          }),
        },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: "Failed to update status" }));
        throw new Error(errorData.error || errorData.details || "Failed to update status");
      }

      // Update local state instead of refetching
      setBooking((prev: any) => ({
        ...prev,
        payment_status_id: parseInt(selectedPaymentStatus),
        payment_status: newStatus,
      }));

      toast.success("Payment status updated");

      // Refresh activities only if that tab is active
      if (activeRightTab === "activities") {
        fetchActivities();
      }

      setIsEditPaymentStatusModalOpen(false);
      setStatusNote("");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to update payment status");
    }
  };

  const handlePaymentMethodUpdate = async () => {
    if (updatingPaymentMethod) return;

    setUpdatingPaymentMethod(true);
    try {
      const oldMethod = booking.payment_method || "Not set";
      const newMethod = selectedPaymentMethod;

      const response = await fetch(
        `/api/staff/venues/${venueId}/bookings/${bookingId}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            payment_method: newMethod,
            old_payment_method: oldMethod,
            new_payment_method: newMethod,
            reason: statusNote || "",
          }),
        },
      );

      if (!response.ok) throw new Error("Failed to update payment preference");

      // Update local state
      setBooking((prev: any) => ({
        ...prev,
        payment_method: newMethod,
      }));

      toast.success("Payment preference updated");

      // Refresh activities if that tab is active
      if (activeRightTab === "activities") {
        fetchActivities();
      }

      setIsEditPaymentMethodModalOpen(false);
      setStatusNote("");
    } catch (error) {
      toast.error("Failed to update payment preference");
    } finally {
      setUpdatingPaymentMethod(false);
    }
  };

  const handleLanguageUpdate = async () => {
    if (!selectedLanguage) {
      toast.error("Please select a language");
      return;
    }

    setUpdatingLanguage(true);
    try {
      const oldLang = booking.lang || "en";

      const response = await fetch(
        `/api/staff/venues/${venueId}/bookings/${bookingId}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            lang: selectedLanguage,
            old_lang: oldLang,
          }),
        },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: "Failed to update language" }));
        throw new Error(errorData.error || errorData.details || "Failed to update language");
      }

      // Update local state
      setBooking((prev: any) => ({
        ...prev,
        lang: selectedLanguage,
      }));

      toast.success("Booking language updated");

      // Refresh activities if that tab is active
      if (activeRightTab === "activities") {
        fetchActivities();
      }

      setIsEditLanguageModalOpen(false);
      setSelectedLanguage("");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to update language");
    } finally {
      setUpdatingLanguage(false);
    }
  };

  const handleCancelBooking = async () => {
    if (!cancelReason.trim()) {
      toast.error("Please provide a reason for cancellation");
      return;
    }

    try {
      const cancelledStatus = bookingStatuses.find(
        (s) => s.id === STATUS_IDS.BOOKING_CANCELLED,
      );

      const response = await fetch(
        `/api/staff/venues/${venueId}/bookings/${bookingId}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            status_id: STATUS_IDS.BOOKING_CANCELLED,
            old_status_id: booking.status_id,
            old_status_name: booking.booking_status?.name,
            new_status_name: cancelledStatus?.name || "Cancelled",
            reason: cancelReason,
          }),
        },
      );

      if (!response.ok) throw new Error("Failed to cancel booking");

      toast.success("Booking cancelled");
      fetchBookingData(true);
      fetchActivities();

      setIsCancelModalOpen(false);
      setCancelReason("");
    } catch (error) {
      toast.error("Failed to cancel booking");
    }
  };

  const handleArchiveBooking = async () => {
    if (!archiveReason.trim()) {
      toast.error("Please provide a reason for archiving");
      return;
    }

    try {
      const archivedStatus = bookingStatuses.find(
        (s) => s.id === STATUS_IDS.BOOKING_ARCHIVED,
      );

      const response = await fetch(
        `/api/staff/venues/${venueId}/bookings/${bookingId}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            status_id: STATUS_IDS.BOOKING_ARCHIVED,
            old_status_id: booking.status_id,
            old_status_name: booking.booking_status?.name,
            new_status_name: archivedStatus?.name || "Archived",
            reason: archiveReason,
          }),
        },
      );

      if (!response.ok) throw new Error("Failed to archive booking");

      toast.success("Booking archived");
      fetchBookingData(true);
      fetchActivities();

      setIsArchiveModalOpen(false);
      setArchiveReason("");
    } catch (error) {
      toast.error("Failed to archive booking");
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim() || addingNote) return;

    setAddingNote(true);
    try {
      const response = await fetch(
        `/api/staff/venues/${venueId}/bookings/${bookingId}/notes`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            content: newNote.trim(),
          }),
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to add note");
      }

      const result = await response.json();

      if (result.success) {
        // Add the new note to local state
        const note = {
          id: result.data.id,
          content: newNote.trim(),
          author: result.data.author || "Current User",
          timestamp: result.data.created_at || new Date().toISOString(),
          createdBy: result.data.createdBy,
        };

        setNotes([note, ...notes]);
        setNewNote("");
        toast.success("Note added");
      } else {
        throw new Error(result.error || "Failed to add note");
      }
    } catch (error) {
      toast.error((error as Error).message || "Failed to add note");
    } finally {
      setAddingNote(false);
    }
  };

  const handleUpdateNote = async (
    noteId: number,
    content: string,
  ): Promise<boolean> => {
    try {
      const response = await fetch(
        `/api/staff/venues/${venueId}/bookings/${bookingId}/notes`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ noteId, content }),
        },
      );

      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.error || "Failed to update note");
      }

      setNotes((prev) =>
        prev.map((n) =>
          n.id === noteId
            ? {
                ...n,
                content: result.data.content,
                timestamp: result.data.timestamp ?? n.timestamp,
                createdBy: result.data.createdBy ?? n.createdBy,
                updated_at: result.data.updated_at,
              }
            : n,
        ),
      );
      toast.success("Note updated");
      return true;
    } catch (error) {
      toast.error((error as Error).message || "Failed to update note");
      return false;
    }
  };

  const handleDeleteNote = async (noteId: number): Promise<boolean> => {
    try {
      const response = await fetch(
        `/api/staff/venues/${venueId}/bookings/${bookingId}/notes?noteId=${noteId}`,
        { method: "DELETE" },
      );

      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.error || "Failed to delete note");
      }

      setNotes((prev) => prev.filter((n) => n.id !== noteId));
      toast.success("Note deleted");
      return true;
    } catch (error) {
      toast.error((error as Error).message || "Failed to delete note");
      return false;
    }
  };

  const handleHostUpdate = async () => {
    try {
      const oldHost = booking.studio_show_host_bookings?.[0]?.host;
      const newHost = availableHosts.find(
        (h) => (h.user_id || h.id).toString() === selectedHost,
      );

      const response = await fetch(`/api/bookings/${bookingId}/host`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          host_id: selectedHost, // This should be the UUID, not parsed as int
          old_host_id: oldHost?.id,
          old_host_name: oldHost?.name || oldHost?.full_name,
          new_host_name: newHost?.name || newHost?.full_name,
          reason: statusNote || "",
        }),
      });

      if (!response.ok) throw new Error("Failed to update host");

      // Update local state - need to update both places where host is stored
      const updatedBooking = { ...booking };

      // Update host in studio_show_host_bookings
      if (updatedBooking.studio_show_host_bookings?.[0]) {
        updatedBooking.studio_show_host_bookings[0].host = newHost;
      }

      // Update host on the main booking object (this is what bookingDetails uses)
      updatedBooking.host = newHost;

      setBooking(updatedBooking);

      toast.success("Host updated");

      // Refresh activities if that tab is active
      if (activeRightTab === "activities") {
        fetchActivities();
      }

      setIsEditHostModalOpen(false);
      setStatusNote("");
    } catch (error) {
      toast.error("Failed to update host");
    }
  };

  const handleClientUpdate = async (formattedData?: {
    fullName: string;
    email: string;
    phone: string;
    participants?: number;
  }) => {
    // Use formatted data if provided, otherwise fall back to clientFormData
    const dataToUse = formattedData || clientFormData;

    if (!dataToUse.fullName.trim()) {
      toast.error("Please provide a client name");
      return;
    }

    try {
      if (!booking.customer?.id) {
        toast.error("Customer ID not found");
        return;
      }

      const venueId = booking.studio_show_host_bookings?.[0]?.studios?.venue_id ||
                     booking.studio_show_host_bookings?.[0]?.studios?.venues?.id;

      const effectiveVenueId = (!venueId || venueId === 0)
        ? window.location.pathname.match(/\/venue\/(\d+)\//)?.[1] || "0"
        : venueId;

      // Booking-scoped: changing the email moves this booking to the right
      // customer instead of renaming the account other bookings share.
      const response = await fetch(`/api/staff/venues/${effectiveVenueId}/bookings/${booking.id}/customer`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: dataToUse.fullName.trim(),
          phone: dataToUse.phone || null, // Don't trim the formatted phone
          email: dataToUse.email.trim() || null,
        }),
      });

      const result = await response.json().catch(() => ({}));
      if (!response.ok || !result.success) {
        toast.error(result.error || "Failed to update client information");
        return;
      }

      // Show the customer the booking actually belongs to now
      const updatedCustomer = result.data?.customer;
      setBooking((prev: any) => ({
        ...prev,
        customer_id: updatedCustomer?.id ?? prev.customer_id,
        customer: {
          ...prev.customer,
          ...(updatedCustomer && {
            id: updatedCustomer.id,
            name: updatedCustomer.name,
            email: updatedCustomer.email,
            phone: updatedCustomer.phone,
          }),
        },
      }));

      // Update participants if changed
      if (dataToUse.participants !== undefined && dataToUse.participants !== booking.participant_count) {
        const updateResponse = await fetch(`/api/staff/venues/${effectiveVenueId}/bookings/${booking.id}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            participants: dataToUse.participants,
            // Add road show flag if this is a road show booking
            ...(isRoadShow && { isRoadShowUpdate: true }),
          }),
        });

        if (!updateResponse.ok) {
          toast.error("Failed to update participants count");
        } else {
          // Update local state
          setBooking((prev: any) => ({
            ...prev,
            participant_count: dataToUse.participants,
          }));
        }
      }

      if (result.notice) {
        toast.warning(result.notice, { duration: 8000 });
        // The booking now belongs to another customer: reload its
        // company details and history
        fetchBookingData(true);
      } else {
        toast.success("Customer information updated successfully");
      }

      setIsEditClientModalOpen(false);
    } catch (error) {
      toast.error("Failed to update client information");
    }
  };

  const handleDateTimeUpdate = async (date: string, startTime: string, endTime: string, reason?: string) => {
    try {
      // Get the existing host booking ID
      const hostBooking = booking?.studio_show_host_bookings?.[0];
      if (!hostBooking) {
        toast.error("Host booking not found");
        return;
      }

      // Store old values for activity logging
      const oldDate = hostBooking.show_session_timeslot?.split("T")[0] || "Not set";
      const oldTime = hostBooking.show_session_timeslot?.split("T")[1]?.substring(0, 5) || "Not set";

      // Create the new timeslot
      const newTimeslot = `${date}T${startTime}:00.000Z`;

      // For road shows, always use the venueId from URL since booking.venue_id might be 0
      const venueIdToUse = venueId; // Use the venueId from props which comes from URL

      // Build the update payload differently for road shows
      let updatePayload: any;

      if (isRoadShow) {
        // For road shows, only update essential fields to avoid triggering pricing logic
        updatePayload = {
          // Store road show times in details field as JSON
          details: JSON.stringify({
            roadShowStartTime: startTime,
            roadShowEndTime: endTime,
            roadShowDate: date
          }),
          // Also update notes for display purposes
          notes: `Road Show Booking\nDate: ${date}\nTime: ${startTime} - ${endTime}`,
          bookingType: "roadshow",
          priceMode: "fixed",
          skipPricingRecalculation: true,
          isRoadShowUpdate: true, // Special flag for road show updates
          ...(reason && { dateTimeChangeReason: reason }), // Include reason if provided

          // Update the show session data
          shows: [{
            showId: hostBooking.show_id || 1,
            showSessionTimeslot: newTimeslot,
            // Only include the IDs, not pricing fields that might trigger recalculation
            assignedPricingId: hostBooking.assigned_pricing_id || 1,
            showTimeSlotPricingId: hostBooking.show_time_slot_pricing_id || 1,
            pricePointId: hostBooking.price_point_id || 1,
            // Keep the existing price unchanged
            price: hostBooking.price || 0,
            useExistingPrice: true,
            date: date,
            time: startTime,
            dayOfWeek: new Date(date).getDay(),
          }],
        };
      } else {
        // Regular booking update
        updatePayload = {
          shows: [{
            showId: hostBooking.show_id || 1,
            showSessionTimeslot: newTimeslot,
            assignedPricingId: hostBooking.assigned_pricing_id || 1,
            showTimeSlotPricingId: hostBooking.show_time_slot_pricing_id || 1,
            pricePointId: hostBooking.price_point_id || 1,
            price: hostBooking.price || 0,
            date: date,
            time: startTime,
            dayOfWeek: new Date(date).getDay(),
          }],
          notes: booking.notes || "",
          ...(reason && { dateTimeChangeReason: reason }), // Include reason for regular bookings too
        };
      }

      const response = await fetch(
        `/api/staff/venues/${venueIdToUse}/bookings/${bookingId}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(updatePayload),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: "Unknown error" }));
        console.error("Date/time update failed:", {
          status: response.status,
          error: errorData,
          payload: updatePayload
        });
        throw new Error(`Failed to update date/time: ${errorData.error || response.statusText}`);
      }

      await fetchBookingData(true);

      // Log the activity using the same venueId
      const activityResponse = await fetch(
        `/api/staff/venues/${venueIdToUse}/bookings/${bookingId}/activities`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            activity: {
              booking_id: parseInt(bookingId),
              activity_type: "road_show_datetime_updated",
              description: `Road show date/time updated from ${oldDate} ${oldTime} to ${date} ${startTime}-${endTime}`,
              metadata: {
                changes: {
                  date: { old: oldDate, new: date },
                  time: { old: oldTime, new: `${startTime}-${endTime}` },
                },
              },
              updated_by: "Staff",
            },
          }),
        }
      );

      if (!activityResponse.ok) {
        console.error("Failed to log activity");
      }

      toast.success("Date and time updated successfully");

      // Refresh activities if that tab is active
      if (activeRightTab === "activities") {
        fetchActivities();
      }

      setIsEditDateTimeModalOpen(false);
    } catch (error) {
      console.error("Error updating date/time:", error);
      toast.error("Failed to update date and time");
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-500">Loading booking details...</p>
      </div>
    );
  }

  // Error state
  if (error || !booking) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-500 mb-4">{error || "Booking not found"}</p>
          <Link
            href={`/${window.location.pathname.split("/")[1]}/venue/${fromVenueId || venueId}/bookings`}
          >
            <Button variant="outline">Back to Bookings</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Content - no separate header */}
      <div className="px-6 py-4">
        <div className="max-w-7xl mx-auto">
          {/* Main content grid */}
          <div
            className="grid gap-4"
            style={{ gridTemplateColumns: "3fr 1fr" }}
          >
            {/* Left Column */}
            <div className="space-y-4">
              {/* Header section */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <Link
                    href={`/${window.location.pathname.split("/")[1]}/venue/${fromVenueId || venueId}/bookings`}
                  >
                    <Button variant="ghost" size="sm" className="p-2">
                      <ArrowLeft className="h-4 w-4" />
                    </Button>
                  </Link>
                  <h1 className="text-2xl font-bold">Booking Details</h1>
                </div>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button>
                      Quick Actions
                      <ChevronDown className="ml-2 h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="z-50">
                    {!isRoadShow && (
                      <DropdownMenuItem onClick={() => {
                        if (!booking?.id) {
                          toast.error('Booking data not loaded yet. Please try again.');
                          return;
                        }
                        setIsEmailModalOpen(true);
                      }}>
                        Send Email
                      </DropdownMenuItem>
                    )}
                    {booking.status_id === STATUS_IDS.BOOKING_PRELIMINARY &&
                    booking.payment_status_id === STATUS_IDS.PAYMENT_PENDING ? (
                      <DropdownMenuItem
                        onClick={() => setIsArchiveModalOpen(true)}
                      >
                        Archive booking
                      </DropdownMenuItem>
                    ) : (
                      booking.status_id !== STATUS_IDS.BOOKING_CANCELLED &&
                      booking.status_id !== STATUS_IDS.BOOKING_ARCHIVED && (
                        <DropdownMenuItem
                          onClick={() => setIsCancelModalOpen(true)}
                        >
                          Cancel booking
                        </DropdownMenuItem>
                      )
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              {/* Booking Info Card */}
              <BookingInfoCard
                booking={booking}
                bookingDetails={bookingDetails}
                bookingStatuses={bookingStatuses}
                paymentStatuses={paymentStatuses}
                onEditBookingStatus={() => {
                  setSelectedBookingStatus(booking.status_id?.toString() || "");
                  setIsEditBookingStatusModalOpen(true);
                }}
                onEditPaymentStatus={() => {
                  setSelectedPaymentStatus(
                    booking.payment_status_id?.toString() || "",
                  );
                  setIsEditPaymentStatusModalOpen(true);
                }}
                onEditPaymentMethod={() => {
                  setSelectedPaymentMethod(booking.payment_method || "card");
                  setIsEditPaymentMethodModalOpen(true);
                }}
                onEditHost={() => {
                  const currentHost =
                    booking.studio_show_host_bookings?.[0]?.host;
                  // Use user_id if available, fallback to id
                  setSelectedHost((currentHost?.user_id || currentHost?.id)?.toString() || "");
                  setIsEditHostModalOpen(true);
                }}
                onEditDateTime={() => {
                  setIsEditDateTimeModalOpen(true);
                }}
                onEditLanguage={() => {
                  setSelectedLanguage(booking.lang || "en");
                  setIsEditLanguageModalOpen(true);
                }}
                getStatusColor={getStatusColor}
                getPaymentIcon={getPaymentIcon}
                canEdit={canEdit}
                canViewPaymentDetails={canViewPaymentDetails}
              />

              {/* External Customer Notes - moved higher for visibility */}
              <ExternalNotesCard
                booking={booking}
                canEdit={canEdit}
              />

              {/* Client Info Card */}
              <ClientInfoCard
                booking={booking}
                clientFormData={clientFormData}
                onEdit={() => {
                  // Initialize form data with current values
                  setClientFormData({
                    fullName: booking.customer?.name || booking.customers?.name || "",
                    email: booking.customer?.email || booking.customers?.email || "",
                    phone: booking.customer?.phone || booking.customers?.phone || "",
                    participants: booking.participant_count,
                  });
                  setIsEditClientModalOpen(true);
                }}
                canEdit={canEdit}
                hideParticipants={true}
              />

              {/* Company Info Card - for corporate bookings and all road shows */}
              {(booking.customer_types?.name === CUSTOMER_TYPES.CORPORATE ||
                booking.customer_type_id === CUSTOMER_TYPE_IDS.CORPORATE ||
                isRoadShow) && (
                <CompanyInfoCard
                  booking={booking}
                  customer={booking.customer}
                  onEdit={companyEdit.open}
                  canEdit={canEdit}
                  venueCurrency={bookingDetails?.currency}
                  venueDefaultLanguage={venueDefaultLanguage}
                />
              )}

              {/* Shows Table - shown for all booking types, including road shows
                  (ShowsCard already handles the road-show event label/times). */}
              <ShowsCard
                booking={booking}
                bookingDetails={bookingDetails}
                venueCurrency={bookingDetails?.currency || "SEK"}
                venueDefaultLanguage={venueDefaultLanguage}
              />

              {/* Add-ons - shown for all booking types (including road shows)
                  whenever the venue has add-ons available. */}
              {hasVenueAddOns ? (
                <AddOnsCard
                  bookingId={bookingId}
                  venueId={venueId}
                  addOns={(booking.booking_add_ons || []).filter(
                    (addon: any) => !addon.is_archived && !addon._isArchived,
                  )}
                  currency={bookingDetails?.currency}
                  customerTypeId={booking.customer_type_id}
                  onUpdate={() => fetchBookingData(true)}
                  hideActions={false}
                  canEdit={canEdit}
                />
              ) : null}

              {/* Shop Items - only show if venue has shop items available and not a road show */}
              {hasVenueShopItems && !isRoadShow ? (
                <ShopItemsCard
                  bookingId={bookingId}
                  venueId={venueId}
                  shopItems={(booking.booking_shop_items || []).filter(
                    (item: any) => !item.is_archived && !item._isArchived,
                  )}
                  currency={bookingDetails?.currency}
                  booking={booking}
                  onUpdate={() => fetchBookingData(true)}
                  onOptimisticUpdate={(updatedShopItems) => {
                    // Optimistically update booking state to reflect shop item changes
                    setBooking((prevBooking: any) => ({
                      ...prevBooking,
                      booking_shop_items: updatedShopItems,
                    }));
                  }}
                />
              ) : null}

              {/* Price Summary - hide for road shows */}
              {!isRoadShow ? (
                <PriceSummaryCard
                  booking={booking}
                  bookingDetails={bookingDetails}
                />
              ) : null}

              {/* Payment Information */}
              {canViewPaymentDetails && (
                <PaymentInfoCard
                  booking={booking}
                  bookingDetails={bookingDetails}
                  getPaymentIcon={getPaymentIcon}
                  onPaymentStatusUpdate={() => fetchBookingData(true)}
                  invoiceData={invoiceData}
                  invoiceLoading={invoiceLoading}
                  onInvoiceGenerated={checkInvoiceExists}
                  isRoadShow={isRoadShow}
                  venueDefaultLanguage={venueDefaultLanguage}
                />
              )}
            </div>

            {/* Right Column - Notes & Activity */}
            <div className="w-full mt-1 sticky top-6" style={{ height: "calc(100vh - 6rem)" }}>
              <Tabs
                value={activeRightTab}
                onValueChange={setActiveRightTab}
                className="w-full h-full flex flex-col"
              >
                <TabsList className="w-full flex-shrink-0">
                  <TabsTrigger value="notes" className="flex-1">
                    Internal Notes
                  </TabsTrigger>
                  <TabsTrigger value="activities" className="flex-1">
                    Activity
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="notes" className="mt-3 flex-1 overflow-hidden">
                  <NotesTab
                    notes={notes}
                    newNote={newNote}
                    setNewNote={setNewNote}
                    onAddNote={handleAddNote}
                    isAddingNote={addingNote}
                    currentUserId={currentUserInfoId}
                    currentUserRoleId={currentUserRoleId}
                    onDeleteNote={handleDeleteNote}
                    onUpdateNote={handleUpdateNote}
                  />
                </TabsContent>

                <TabsContent value="activities" className="mt-3 flex-1 overflow-hidden">
                  <ActivityTab
                    activities={activities}
                    loading={activitiesLoading}
                  />
                </TabsContent>
              </Tabs>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <EditBookingStatusModal
        isOpen={isEditBookingStatusModalOpen}
        onClose={() => {
          setIsEditBookingStatusModalOpen(false);
          setStatusNote("");
        }}
        selectedStatus={selectedBookingStatus}
        setSelectedStatus={setSelectedBookingStatus}
        statusNote={statusNote}
        setStatusNote={setStatusNote}
        bookingStatuses={bookingStatuses}
        onConfirm={handleBookingStatusUpdate}
        showStartTime={
          booking?.studio_show_host_bookings?.[0]?.show_session_timeslot ||
          booking?.show_session_timeslot ||
          booking?.booking_date
        }
      />

      <EditPaymentStatusModal
        isOpen={isEditPaymentStatusModalOpen}
        onClose={() => {
          setIsEditPaymentStatusModalOpen(false);
          setStatusNote("");
        }}
        selectedStatus={selectedPaymentStatus}
        setSelectedStatus={setSelectedPaymentStatus}
        statusNote={statusNote}
        setStatusNote={setStatusNote}
        paymentStatuses={paymentStatuses}
        onConfirm={handlePaymentStatusUpdate}
      />

      <EditPaymentMethodModal
        isOpen={isEditPaymentMethodModalOpen}
        onClose={() => {
          setIsEditPaymentMethodModalOpen(false);
          setStatusNote("");
        }}
        selectedMethod={selectedPaymentMethod}
        setSelectedMethod={setSelectedPaymentMethod}
        statusNote={statusNote}
        setStatusNote={setStatusNote}
        onConfirm={handlePaymentMethodUpdate}
        isCorporate={booking.customer_types?.name === CUSTOMER_TYPES.CORPORATE}
        isRoadShow={isRoadShow}
        isLoading={updatingPaymentMethod}
      />

      <EditLanguageModal
        isOpen={isEditLanguageModalOpen}
        onClose={() => {
          setIsEditLanguageModalOpen(false);
          setSelectedLanguage("");
        }}
        selectedLanguage={selectedLanguage}
        setSelectedLanguage={setSelectedLanguage}
        supportedLanguages={venueLanguages}
        onConfirm={handleLanguageUpdate}
        isLoading={updatingLanguage}
      />

      <CancelBookingModal
        isOpen={isCancelModalOpen}
        onClose={() => {
          setIsCancelModalOpen(false);
          setCancelReason("");
        }}
        cancelReason={cancelReason}
        setCancelReason={setCancelReason}
        onConfirm={handleCancelBooking}
        venueDefaultLanguage={venueDefaultLanguage}
      />

      <ArchiveBookingModal
        isOpen={isArchiveModalOpen}
        onClose={() => {
          setIsArchiveModalOpen(false);
          setArchiveReason("");
        }}
        archiveReason={archiveReason}
        setArchiveReason={setArchiveReason}
        onConfirm={handleArchiveBooking}
      />

      <SendEmailModal
        isOpen={isEmailModalOpen}
        onClose={() => {
          setIsEmailModalOpen(false);
          setEmailSubject("");
          setEmailBody("");
        }}
        emailSubject={emailSubject}
        setEmailSubject={setEmailSubject}
        emailBody={emailBody}
        setEmailBody={setEmailBody}
        customerEmail={clientFormData.email}
        bookingId={booking?.id}
        bookingLanguage={booking?.lang}
      />

      <EditHostModal
        isOpen={isEditHostModalOpen}
        onClose={() => {
          setIsEditHostModalOpen(false);
          setStatusNote("");
        }}
        selectedHost={selectedHost}
        setSelectedHost={setSelectedHost}
        statusNote={statusNote}
        setStatusNote={setStatusNote}
        availableHosts={availableHosts}
        onConfirm={handleHostUpdate}
      />

      <EditClientModal
        isOpen={isEditClientModalOpen}
        onClose={() => {
          setIsEditClientModalOpen(false);
        }}
        clientFormData={clientFormData}
        setClientFormData={setClientFormData}
        onConfirm={handleClientUpdate}
        isRoadShow={isRoadShow}
      />

      <EditCompanyModal
        isOpen={companyEdit.isOpen}
        onClose={companyEdit.close}
        companyFormData={companyEdit.companyFormData}
        setCompanyFormData={companyEdit.setCompanyFormData}
        onConfirm={companyEdit.save}
        venueCurrency={bookingDetails?.currency}
        venueDefaultLanguage={venueDefaultLanguage}
        paymentMethod={booking?.payment_method}
        partner={
          companyEdit.canEditPartner
            ? {
                options: companyEdit.partners,
                current: booking?.partner ?? null,
                selectedId: companyEdit.partnerId,
                onSelect: companyEdit.selectPartner,
                endCustomerName: companyEdit.endCustomerName,
                onEndCustomerNameChange: companyEdit.setEndCustomerName,
              }
            : undefined
        }
      />

      {/* Edit Date/Time Modal for Road Shows */}
      <EditDateTimeModal
        isOpen={isEditDateTimeModalOpen}
        onClose={() => {
          setIsEditDateTimeModalOpen(false);
        }}
        currentDate={
          booking?.studio_show_host_bookings?.[0]?.show_session_timeslot?.split("T")[0] || null
        }
        currentStartTime={
          booking?.studio_show_host_bookings?.[0]?.show_session_timeslot?.split("T")[1]?.substring(0, 5) || null
        }
        currentEndTime={(() => {
          // Extract end time from notes
          if (booking?.notes) {
            const timeMatch = booking.notes.match(/Time:\s*(\d{2}:\d{2})\s*-\s*(\d{2}:\d{2})/);
            if (timeMatch && timeMatch[2]) {
              return timeMatch[2];
            }
          }
          return "17:00"; // Default end time
        })()}
        onConfirm={handleDateTimeUpdate}
      />
    </div>
  );
}
